import { notesAfterDelete } from '@/lib/noteThreads';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  addWebsiteSignupNote,
  cancelWebsiteSignup,
  deleteWebsiteSignup,
  deleteWebsiteSignupNote,
  getWebsiteSignup,
  promoteWebsiteSignup,
  resendRegistrationConfirmation,
  updateWebsiteSignup,
  updateWebsiteSignupNote,
} from '@/lib/api';
import {
  ATTENDANCE_OPTIONS,
  HEARD_ABOUT_OPTIONS,
  displayValue,
  formatDateTime,
  fullName,
  heardAboutLabel,
  signupStatusChoices,
  signupStatusMeta,
  sourcePageLabel,
} from '@/lib/websiteEngagement';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import NotesThread from '@/components/admin/NotesThread';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import {
  CancelRegistrationDialog,
  DeleteRegistrationDialog,
} from '@/components/admin/RegistrationActions';
import { StatusPill, adminInputClass } from '@/components/admin/AdminTableStyles';
import {
  ArrowLeft, ArrowUpCircle, Ban, CalendarCheck, ChevronDown, Loader2, Save, Send, Trash2,
} from 'lucide-react';

const inputClass = adminInputClass;

// Everything the visitor can send, plus the staff-owned status. Ids and the
// audit timestamps are deliberately absent: the server rejects them anyway, and
// notes are their own threaded system rather than a field on this form.
const EDITABLE_FIELDS = [
  'first_name', 'last_name', 'email', 'phone_number', 'attendance_preference',
  'how_did_you_hear_about_us', 'how_did_you_hear_about_us_detail', 'agency_name',
  'event_id', 'event_title', 'event_date', 'timezone', 'source_page',
  'consent_to_contact', 'status',
];

function toForm(signup) {
  const form = {};
  EDITABLE_FIELDS.forEach((field) => {
    form[field] = signup?.[field] ?? (field === 'consent_to_contact' ? false : '');
  });
  return form;
}

/** Only what actually changed, so a save never rewrites untouched fields. */
export function changedFields(original, form) {
  const changes = {};
  EDITABLE_FIELDS.forEach((field) => {
    const before = original?.[field] ?? (field === 'consent_to_contact' ? false : '');
    const after = form?.[field] ?? (field === 'consent_to_contact' ? false : '');
    if (before !== after) changes[field] = after;
  });
  return changes;
}

/** A read-only row. Empty values read "Not provided" rather than disappearing. */
function ReadOnlyField({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">{label}</p>
      <p className="break-words text-sm text-[#062B1A]/80">{displayValue(value)}</p>
    </div>
  );
}

export default function AdminWebsiteSignupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [signup, setSignup] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getWebsiteSignup(id);
      setSignup(data);
      setForm(toForm(data));
      setNotFound(false);
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(error?.response?.data?.detail || 'Could not load this signup');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const changes = useMemo(() => changedFields(signup, form), [signup, form]);
  const isDirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!isDirty || saving) return;
    try {
      setSaving(true);
      const { data } = await updateWebsiteSignup(id, changes);
      setSignup(data);
      setForm(toForm(data));
      toast.success('Signup saved');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Could not save this signup. Check the fields and try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Cancel keeps this record and gives the seat back; delete removes the
  // person's data for good. Neither emails anyone. Promoting and resending
  // both do email the registrant — that is the point of them.
  const permissions = signup?.permissions || {};
  const canCancel = permissions['registrations.cancel'] !== false;
  const canPromote = permissions['registrations.promote'] !== false;
  const canResend = permissions['registrations.resend_confirmation'] !== false;
  const canDelete = permissions['registrations.delete'] === true;
  const deleteHint = (signup?.permission_hints || {})['registrations.delete']
    || 'Only a full administrator can permanently delete a registration.';

  const runCancel = async ({ reason }) => {
    try {
      setActionBusy(true);
      const { data } = await cancelWebsiteSignup(id, { reason });
      toast.success(data?.already_cancelled
        ? 'This registration was already cancelled'
        : 'Registration cancelled');
      setCancelOpen(false);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not cancel this registration');
    } finally {
      setActionBusy(false);
    }
  };

  // A full event refuses the promotion rather than overbooking silently; the
  // confirm here is how staff say they meant it.
  const runPromote = async () => {
    try {
      setActionBusy(true);
      const { data } = await promoteWebsiteSignup(id, {});
      toast.success(data?.email_sent
        ? 'Promoted from the waitlist and emailed'
        : 'Promoted from the waitlist — the confirmation email did not send');
      await load();
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && typeof detail === 'string'
        && window.confirm(`${detail}\n\nPromote anyway and go over capacity?`)) {
        try {
          const { data } = await promoteWebsiteSignup(id, { allow_overbook: true });
          toast.success(data?.email_sent
            ? 'Promoted over capacity and emailed'
            : 'Promoted over capacity — the confirmation email did not send');
          await load();
        } catch (retryError) {
          console.error(retryError);
          toast.error(retryError?.response?.data?.detail || 'Could not promote this registration');
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not promote this registration');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const runResend = async () => {
    try {
      setActionBusy(true);
      await resendRegistrationConfirmation(id);
      toast.success('Confirmation resent');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not resend the confirmation');
    } finally {
      setActionBusy(false);
    }
  };

  const runDelete = async (payload) => {
    try {
      setActionBusy(true);
      await deleteWebsiteSignup(id, payload);
      toast.success('Registration permanently deleted');
      navigate('/admin/website-signups');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete this registration');
      setActionBusy(false);
    }
  };

  // Notes go through the shared threaded system: same component, same mention
  // notifications as application, student and agency notes.
  const notes = signup?.notes || [];

  const addNote = async (text, parentId) => {
    try {
      const { data } = await addWebsiteSignupNote(id, { body: text, parent_id: parentId || null });
      setSignup((current) => ({ ...current, notes: [...(current?.notes || []), data] }));
      toast.success('Note added');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not add the note');
    }
  };

  const editNote = async (note, text) => {
    try {
      const { data } = await updateWebsiteSignupNote(id, note.id, { body: text });
      setSignup((current) => ({
        ...current,
        notes: (current?.notes || []).map((item) => (item.id === note.id ? data : item)),
      }));
      toast.success('Note updated');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not update the note');
    }
  };

  const removeNote = async (note) => {
    try {
      setDeletingNoteId(note.id);
      await deleteWebsiteSignupNote(id, note.id);
      setSignup((current) => ({
        ...current,
        notes: notesAfterDelete(current?.notes, note.id),
      }));
      toast.success('Note deleted');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not delete the note');
    } finally {
      setDeletingNoteId('');
    }
  };

  // "Their wording" only earns space when there is wording worth showing: the
  // visitor chose Other, or wrote something the server could not map.
  const rawWording = String(form.how_did_you_hear_about_us_detail || '').trim();
  const showOriginalResponse = form.how_did_you_hear_about_us === 'other' || !!rawWording;
  // Audit Details exists for the case where the stored answer and the visitor's
  // words genuinely differ — an answer we normalized into something else.
  const wordingDiffers = !!rawWording && form.how_did_you_hear_about_us !== 'other';

  if (loading) {
    return (
      <div className="animate-fade-in space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-[#F7F8FA]" />
        <div className="h-72 animate-pulse rounded-xl bg-[#F7F8FA]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="animate-fade-in space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/website-signups')}
          className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to signups
        </Button>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-10 text-center text-sm text-[#062B1A]/55">
            This signup no longer exists.
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = signupStatusMeta(signup?.status);

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/website-signups')}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to signups
          </Button>
          <h2
            className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <CalendarCheck className="h-6 w-6 text-[#D4AF37]" />
            {displayValue(fullName(signup))}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>
            {/* Whether this person holds a seat is a different question from
                their follow-up status, so it gets its own pill. */}
            {signup?.waitlisted && signup?.status !== 'cancelled' && (
              <StatusPill tone="amber">Waitlisted</StatusPill>
            )}
            {signup?.registration_id && (
              <span className="font-mono text-xs text-[#062B1A]/60">{signup.registration_id}</span>
            )}
            <span className="text-xs text-[#062B1A]/55">
              Registered {formatDateTime(signup?.created_at)}
            </span>
          </div>
        </div>
        <PortalEmailComposer
          audience="event_registrations"
          recipients={[signup]}
          buttonLabel="Email attendee"
          buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
        />
        <Button
          onClick={save}
          disabled={!isDirty || saving}
          className="bg-[#062B1A] text-white hover:bg-[#041B10]"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      <div
        className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]"
        data-testid="signup-detail-columns"
      >
        <div className="space-y-6">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Contact
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="first_name">First name</Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={(event) => setField('first_name', event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last_name">Last name</Label>
                  <Input
                    id="last_name"
                    value={form.last_name}
                    onChange={(event) => setField('last_name', event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(event) => setField('email', event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone_number">Phone</Label>
                  <Input
                    id="phone_number"
                    value={form.phone_number}
                    onChange={(event) => setField('phone_number', event.target.value)}
                    placeholder="Not provided"
                    className={inputClass}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Registration
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="attendance_preference">Attendance preference</Label>
                  <Select
                    value={form.attendance_preference || 'undecided'}
                    onValueChange={(value) => setField('attendance_preference', value)}
                  >
                    <SelectTrigger id="attendance_preference" aria-label="Attendance preference" className={inputClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ATTENDANCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="how_did_you_hear_about_us">Referral source</Label>
                  <Select
                    value={form.how_did_you_hear_about_us || 'other'}
                    onValueChange={(value) => setField('how_did_you_hear_about_us', value)}
                  >
                    <SelectTrigger id="how_did_you_hear_about_us" aria-label="Referral source" className={inputClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HEARD_ABOUT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Only meaningful for an agency referral — showing it on every
                    record invited staff to fill in a field that means nothing. */}
                {form.how_did_you_hear_about_us === 'agency' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="agency_name">Agency name</Label>
                    <Input
                      id="agency_name"
                      value={form.agency_name}
                      onChange={(event) => setField('agency_name', event.target.value)}
                      placeholder="Not provided"
                      className={inputClass}
                    />
                    {!String(form.agency_name || '').trim() && (
                      <p className="text-xs text-[rgb(153,27,27)]">
                        Required when an agency referred this person.
                      </p>
                    )}
                  </div>
                )}

                {/* Only shown when there is wording to show: the visitor picked
                    "Other", or typed something we could not map to an option. */}
                {showOriginalResponse && (
                  <div className="space-y-1.5">
                    <Label htmlFor="how_did_you_hear_about_us_detail">Original response</Label>
                    <Input
                      id="how_did_you_hear_about_us_detail"
                      value={form.how_did_you_hear_about_us_detail}
                      onChange={(event) => setField('how_did_you_hear_about_us_detail', event.target.value)}
                      placeholder="Not provided"
                      className={inputClass}
                    />
                    <p className="text-xs text-[#062B1A]/45">What the visitor typed, word for word.</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] px-3 py-2.5">
                <Switch
                  id="consent_to_contact"
                  checked={!!form.consent_to_contact}
                  onCheckedChange={(value) => setField('consent_to_contact', !!value)}
                  aria-label="Consent to contact"
                />
                <div>
                  <Label htmlFor="consent_to_contact" className="text-sm text-[#062B1A]">
                    Consented to be contacted
                  </Label>
                  <p className="text-xs text-[#062B1A]/55">
                    Given on the website form. Turn off only if this person withdraws consent.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Event
              </h3>
              {/* What this registration is linked to, as staff read it. The
                  link itself is the event UUID, in Audit details below. */}
              {(signup?.event_group_label || signup?.event_staff_reference_code) && (
                <div
                  className="rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] px-3 py-2.5"
                  data-testid="signup-event-group"
                >
                  <p className="text-sm font-semibold text-[#062B1A]">
                    {signup?.event_group_label}
                  </p>
                  {signup?.event_staff_reference_code && (
                    <p className="font-mono text-xs text-[#062B1A]/55">
                      {signup.event_staff_reference_code}
                      <span className="ml-2 font-sans not-italic">staff reference — internal only</span>
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="event_title">Event title</Label>
                  <Input
                    id="event_title"
                    value={form.event_title}
                    onChange={(event) => setField('event_title', event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event_id">Event system id</Label>
                  <Input
                    id="event_id"
                    value={form.event_id}
                    onChange={(event) => setField('event_id', event.target.value)}
                    className={inputClass}
                  />
                  <p className="text-xs text-[#062B1A]/45">
                    Display only. Registrations are linked by event UUID.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event_date">Event date</Label>
                  <Input
                    id="event_date"
                    type="date"
                    value={form.event_date}
                    onChange={(event) => setField('event_date', event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    value={form.timezone}
                    onChange={(event) => setField('timezone', event.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="source_page">Source page</Label>
                  <Input
                    id="source_page"
                    value={form.source_page}
                    onChange={(event) => setField('source_page', event.target.value)}
                    placeholder="Not provided"
                    className={inputClass}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Follow-up
              </h3>
              <div className="space-y-1.5">
                <Label htmlFor="status">Status</Label>
                <Select value={form.status || 'new'} onValueChange={(value) => setField('status', value)}>
                  <SelectTrigger id="status" aria-label="Status" className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {signupStatusChoices(signup?.status).map((status) => (
                      <SelectItem key={status} value={status}>{signupStatusMeta(status).label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {signup?.status === 'cancelled' ? (
                  <p className="text-xs text-[#062B1A]/50">
                    Moving this off Cancelled reinstates the registration and takes the seat back.
                  </p>
                ) : (
                  <p className="text-xs text-[#062B1A]/50">
                    To cancel, use the Cancel registration action below — it frees the seat and
                    records the reason.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Out in the open rather than behind a hover menu: these are the two
              actions staff come to this page to take. */}
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm" data-testid="signup-actions">
            <CardContent className="space-y-3 p-5">
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Actions
              </h3>
              {/* Only where there is a seat to give: a waitlisted person on a
                  live registration. This is the action that sends them the
                  joining details they have not been given yet. */}
              {signup?.waitlisted && signup?.status !== 'cancelled' && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!canPromote || actionBusy}
                    onClick={runPromote}
                    data-testid="promote-registration"
                    className="w-full justify-start border-[rgba(6,43,26,0.25)] text-[#062B1A]"
                  >
                    <ArrowUpCircle className="mr-2 size-4" /> Promote from waitlist
                  </Button>
                  <p className="text-xs text-[#062B1A]/50">
                    Moves this person into a seat and emails them the joining details.
                  </p>
                </>
              )}
              {signup?.event_uuid && signup?.status !== 'cancelled' && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canResend || actionBusy}
                  onClick={runResend}
                  data-testid="resend-confirmation"
                  className="w-full justify-start border-[rgba(6,43,26,0.25)] text-[#062B1A]"
                >
                  <Send className="mr-2 size-4" /> Resend confirmation
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                disabled={!canCancel}
                onClick={() => setCancelOpen(true)}
                className="w-full justify-start border-[rgba(138,101,8,0.35)] text-[#8A6508]"
              >
                <Ban className="mr-2 size-4" /> Cancel registration
              </Button>
              <p className="text-xs text-[#062B1A]/50">
                Keeps the record, the notes and the consent evidence, frees the seat, and emails
                nobody.
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={!canDelete}
                title={canDelete ? undefined : deleteHint}
                onClick={() => setDeleteOpen(true)}
                className="w-full justify-start border-[rgba(153,27,27,0.3)] text-[rgb(153,27,27)]"
              >
                <Trash2 className="mr-2 size-4" /> Permanently delete
              </Button>
              <p className="text-xs text-[#062B1A]/50">
                {canDelete
                  ? 'Removes this person’s data for good. Cannot be undone.'
                  : deleteHint}
              </p>
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Submission
              </h3>
              <ReadOnlyField label="Registration ID" value={signup?.registration_id} />
              <ReadOnlyField label="Source" value={signup?.source} />
              <ReadOnlyField label="Submitted from" value={sourcePageLabel(signup?.source_page)} />
              <ReadOnlyField label="Referral source" value={heardAboutLabel(signup?.how_did_you_hear_about_us)} />
              <ReadOnlyField label="Received" value={formatDateTime(signup?.created_at)} />
              <ReadOnlyField label="Last updated" value={formatDateTime(signup?.updated_at)} />

              {/* Folded away by default: the internal id and a normalized
                  answer's original wording are for tracing a record, not for
                  reading it. */}
              <div className="border-t border-[rgba(6,43,26,0.08)] pt-3">
                <button
                  type="button"
                  onClick={() => setAuditOpen((current) => !current)}
                  aria-expanded={auditOpen}
                  className="flex w-full items-center justify-between text-left text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45 hover:text-[#062B1A]/70"
                >
                  Audit details
                  <ChevronDown className={`size-3.5 transition-transform ${auditOpen ? 'rotate-180' : ''}`} />
                </button>
                {auditOpen && (
                  <div className="mt-3 space-y-3" data-testid="audit-details">
                    <ReadOnlyField label="Internal id" value={signup?.id} />
                    {/* The join is the event UUID. Everything else about the
                        event on this record is display or history. */}
                    <ReadOnlyField label="Event UUID" value={signup?.event_uuid} />
                    <ReadOnlyField label="Event system id" value={signup?.event_id} />
                    {signup?.legacy_event_reference && (
                      <ReadOnlyField
                        label="Originally submitted event reference"
                        value={signup.legacy_event_reference}
                      />
                    )}
                    {signup?.cancelled_at && (
                      <>
                        <ReadOnlyField label="Cancelled" value={formatDateTime(signup.cancelled_at)} />
                        <ReadOnlyField label="Cancelled by" value={signup.cancelled_by_name} />
                        <ReadOnlyField label="Cancellation reason" value={signup.cancellation_reason} />
                      </>
                    )}
                    {wordingDiffers && (
                      <ReadOnlyField
                        label="Original response"
                        value={signup?.how_did_you_hear_about_us_detail}
                      />
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Notes always close the profile as a full-width section. Keeping them
          outside the two-column layout prevents the thread from returning to
          the right rail on wide screens. */}
      <Card
        className="border border-[rgba(6,43,26,0.08)] shadow-sm"
        data-testid="signup-notes-bottom"
      >
        <CardContent className="space-y-4 p-5">
          <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Notes
          </h3>
          {/* The same threaded notes used on applications, students and
              agencies — @-mentions notify, replies thread, edits are marked. */}
          <NotesThread
            isSuperAdmin={user?.role === 'admin' && user?.is_super_admin === true}
            notes={notes}
            currentUserId={user?.id || ''}
            onAdd={addNote}
            onEdit={editNote}
            onDelete={removeNote}
            deletingId={deletingNoteId}
            emptyText="No notes yet. Add the first."
          />
        </CardContent>
      </Card>

      <CancelRegistrationDialog
        open={cancelOpen}
        registration={signup}
        busy={actionBusy}
        onCancel={() => setCancelOpen(false)}
        onConfirm={runCancel}
      />
      <DeleteRegistrationDialog
        open={deleteOpen}
        registration={signup}
        eventReference={signup?.event_staff_reference_code}
        busy={actionBusy}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={runDelete}
      />
    </div>
  );
}
